import type { CSSProperties } from "react";

import { cn } from "../base/utils";
import type { DataExplorerViewTreeClassNames } from "../data-explorer";
import type { NavigationSidebarActiveVariant } from "./types";

export const unifiedRow =
  "gap-2 rounded-[var(--radius)] border border-transparent text-sm font-medium motion-reduce:transition-none";
export const unifiedIcon =
  "size-[var(--monkeys-component-navigation-sidebar-icon-size,1.125rem)] shrink-0 text-inherit [&_svg]:size-full";
export const unifiedInactive =
  "text-foreground hover:border-transparent hover:bg-[var(--navigation-internal-hover-background)] hover:text-[var(--navigation-internal-hover-foreground)]";
export const unifiedSelected =
  "border-[var(--navigation-internal-selected-border)] bg-[var(--navigation-internal-selected-background)] text-[var(--navigation-internal-selected-foreground)] hover:border-[var(--navigation-internal-selected-border)] hover:bg-[var(--navigation-internal-selected-background)] hover:text-[var(--navigation-internal-selected-foreground)]";
export const unifiedThemeDefaults =
  "[--navigation-internal-theme-selected-background:color-mix(in_srgb,hsl(var(--monkeys-color-primary,var(--primary)))_10%,transparent)] [--navigation-internal-theme-selected-foreground:hsl(var(--monkeys-color-primary,var(--primary)))] dark:[--navigation-internal-theme-selected-background:hsl(var(--monkeys-color-accent,var(--accent)))] dark:[--navigation-internal-theme-selected-foreground:hsl(var(--monkeys-color-foreground,var(--foreground)))]";

export function unifiedStyle(
  activeVariant?: NavigationSidebarActiveVariant,
): CSSProperties {
  const background = {
    muted: "hsl(var(--monkeys-color-accent,var(--accent)))",
    soft: "color-mix(in srgb,hsl(var(--monkeys-color-primary,var(--primary))) 10%,transparent)",
    solid: "hsl(var(--monkeys-color-primary,var(--primary)))",
    outline: "hsl(var(--monkeys-color-background,var(--background)))",
  }[activeVariant ?? "muted"];
  const foreground =
    activeVariant === "solid"
      ? "hsl(var(--monkeys-color-primary-foreground,var(--primary-foreground)))"
      : "hsl(var(--monkeys-color-foreground,var(--foreground)))";
  const border =
    activeVariant === undefined || activeVariant === "muted"
      ? "transparent"
      : activeVariant === "soft"
        ? "color-mix(in srgb,hsl(var(--monkeys-color-primary,var(--primary))) 25%,transparent)"
        : activeVariant === "outline"
          ? "color-mix(in srgb,hsl(var(--monkeys-color-primary,var(--primary))) 55%,transparent)"
          : "hsl(var(--monkeys-color-primary,var(--primary)))";

  return {
    "--navigation-internal-default-selected-background": activeVariant === undefined
      ? "var(--navigation-internal-theme-selected-background)"
      : background,
    "--navigation-internal-default-selected-foreground": activeVariant === undefined
      ? "var(--navigation-internal-theme-selected-foreground)"
      : foreground,
    "--navigation-internal-selected-background":
      "var(--monkeys-component-navigation-sidebar-row-selected-background,var(--navigation-internal-default-selected-background))",
    "--navigation-internal-selected-foreground":
      "var(--monkeys-component-navigation-sidebar-row-selected-foreground,var(--navigation-internal-default-selected-foreground))",
    "--navigation-internal-selected-border": `var(--monkeys-component-navigation-sidebar-row-selected-border,${border})`,
    "--navigation-internal-hover-background":
      "var(--monkeys-component-navigation-sidebar-row-hover-background,color-mix(in srgb,hsl(var(--monkeys-color-accent,var(--accent))) 65%,transparent))",
    "--navigation-internal-hover-foreground":
      "var(--monkeys-component-navigation-sidebar-row-hover-foreground,hsl(var(--monkeys-color-foreground,var(--foreground))))",
  } as CSSProperties;
}

/** Tree-local fallbacks preserve existing theme overrides without changing route styles. */
const treeStateVariables =
  "[--navigation-internal-selected-background:var(--monkeys-component-navigation-sidebar-row-selected-background,var(--monkeys-component-navigation-sidebar-tree-row-selected-background,var(--navigation-internal-default-selected-background)))] [--navigation-internal-selected-foreground:var(--monkeys-component-navigation-sidebar-row-selected-foreground,var(--monkeys-component-navigation-sidebar-tree-row-selected-foreground,var(--navigation-internal-default-selected-foreground)))] [--navigation-internal-hover-background:var(--monkeys-component-navigation-sidebar-row-hover-background,var(--monkeys-component-navigation-sidebar-tree-row-hover-background,color-mix(in_srgb,hsl(var(--monkeys-color-accent,var(--accent)))_65%,transparent)))] [--navigation-internal-hover-foreground:var(--monkeys-component-navigation-sidebar-row-hover-foreground,var(--monkeys-component-navigation-sidebar-tree-row-hover-foreground,hsl(var(--monkeys-color-foreground,var(--foreground)))))]";

const selectedTreeNode =
  "aria-selected:border-[var(--navigation-internal-selected-border)] aria-selected:bg-[var(--navigation-internal-selected-background)] aria-selected:text-[var(--navigation-internal-selected-foreground)] aria-selected:hover:border-[var(--navigation-internal-selected-border)] aria-selected:hover:bg-[var(--navigation-internal-selected-background)] aria-selected:hover:text-[var(--navigation-internal-selected-foreground)]";
const nestedControl =
  "rounded-[var(--monkeys-component-navigation-sidebar-action-radius,0.25rem)] text-inherit hover:bg-foreground/10 hover:text-inherit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none";

export function unifiedTreeClassNames(
  height: string,
  padding: string,
  overrides: DataExplorerViewTreeClassNames = {},
): DataExplorerViewTreeClassNames {
  const row = cn(unifiedRow, height, padding, unifiedInactive);
  return {
    ...overrides,
    root: cn(treeStateVariables, "space-y-0.5", overrides.root),
    group: cn(
      "space-y-0.5",
      // Indent row contents, keeping guide-free hover and selection full width.
      "[&>[role=treeitem]+[role=group]]:[--data-explorer-tree-group-indent:var(--monkeys-component-navigation-sidebar-tree-group-indent,1rem)]",
      overrides.group,
    ),
    groupHeaderButton: cn("h-auto", row, overrides.groupHeaderButton),
    nodeButton: cn(row, selectedTreeNode, overrides.nodeButton),
    itemSelected: cn(unifiedSelected, overrides.itemSelected),
    nodeIcon: cn(
      unifiedIcon,
      "group-hover/tree-node:text-inherit",
      overrides.nodeIcon,
    ),
    nodeLabel: cn("font-medium", overrides.nodeLabel),
    nodeDescription: cn("text-inherit", overrides.nodeDescription),
    nodeDisabled: cn("opacity-50", overrides.nodeDisabled),
    groupActions: cn(
      nestedControl,
      "order-2 size-5 p-0 group-focus-within/tree-group:opacity-100",
      overrides.groupActions,
    ),
    nodeActions: cn(
      nestedControl,
      "order-2 size-5 p-0 group-focus-within/tree-node:opacity-100",
      overrides.nodeActions,
    ),
    nodeToggle: cn(nestedControl, "order-1 empty:hidden", overrides.nodeToggle),
    expander: cn(nestedControl, "order-1 empty:hidden", overrides.expander),
    nodeDragHandle: cn(nestedControl, overrides.nodeDragHandle),
  };
}
